import "dayjs/locale/en"; // Import the desired locale

import { getItemFromStore, setItemToStore } from "@helpers/utils";

import { DATE_FORMAT_API } from "@src/utility/constants";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import relativeTime from "dayjs/plugin/relativeTime";
import size from "lodash/size";
import timezone from "dayjs/plugin/timezone";
import { useForm } from "react-hook-form";
import { useLayoutEffect } from "react";
import useSearchParams from "./use-search-params";
import utc from "dayjs/plugin/utc";

// Load the plugins 
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const useDateHandler = (props = {}) => {

    const { onSubmit, module = {},  rangeAllowed, defaultHandling, defaultValues = {
        start: new Date(),
        end  : new Date()
    },
    startWithEmptyRange
    } = props;

    const defaultRange = {
        start: startWithEmptyRange ? null : new Date(defaultValues.start),
        end  : startWithEmptyRange ? null : new Date(defaultValues.end)
    };

    const { getSearchParams, createSearchParams } = useSearchParams();
    const { control, watch, setValue } = useForm({
        defaultValues: {
            date: {
                ...defaultRange,
                key: "selection"
            }
        }
    });

    const start = getSearchParams("start") || dayjs(defaultRange.start).format(DATE_FORMAT_API);
    const end = getSearchParams("end") || dayjs(defaultRange.end).format(DATE_FORMAT_API);

    const storedDateRange = getItemFromStore("date-range") || {};
    const moduleWiseStoredDateRange = getItemFromStore("date-range")?.[module.name]?.[module.id] || {};

    /**
     * Function to format date
     * @param date
     * @param type
     */
    const dateFormatter = ({
        date = new Date(),
        formatAs = DATE_FORMAT_API
    }) => {
        return dayjs(date).format(formatAs);
    };
    
    /**
     * A function that is called when the date is changed. It calls the handleFilter function and passes
     * in the date range.
     */
    const handleDateChange = () => {
        if (watch("date.start") && watch("date.end")) {

            createSearchParams("start", dateFormatter({ date: watch("date.start") }));
            createSearchParams("end", dateFormatter({ date: watch("date.end") }));
            if (module) {
                setItemToStore("date-range", {
                    ...storedDateRange,
                    [module.name]: {
                        ...(module.id ? {
                            [module.id]: {
                                start: dateFormatter({ date: watch("date.start") }),
                                end  : dateFormatter({ date: watch("date.end") })
                            }
                        } : {
                            start: dateFormatter({ date: watch("date.start") }),
                            end  : dateFormatter({ date: watch("date.end") })
                        })
                    }
                });
            }
        }
        if (onSubmit) onSubmit(false);
    };

    const validateDateRange = (start, end) => {
        if (start && end) {
            const startDate = dayjs(start);
            const endDate = dayjs(end);
            const diffInDays = endDate.diff(startDate, "day") + 1;
            if (rangeAllowed) {
                return startDate.isValid() && endDate.isValid() && diffInDays >= 0 && diffInDays <= rangeAllowed;
            } else {
                return startDate.isValid() && endDate.isValid() && diffInDays >= 0;
            }
        } else {
            return true;
        }
    };

    /**
     * This function is called by the useLayoutEffect hook on every reload, this is responsible, 
     * for hydrating the component the date range selected by user which is stored in the store.
     */ 
    const handleDefaultDates = () => {
        let validStart;
        let validEnd;
        if (validateDateRange(new Date(start), new Date(end))) {
            validStart = start;
            validEnd = end;
        } else {
            validStart =  size(moduleWiseStoredDateRange) ? moduleWiseStoredDateRange.start : new Date();
            validEnd = size(moduleWiseStoredDateRange) ? moduleWiseStoredDateRange.end : new Date();
        }
        setValue("date.start", new Date(dayjs(validStart)));
        setValue("date.end", new Date(dayjs(validEnd)));
        createSearchParams("start", dateFormatter({ date: validStart }));
        createSearchParams("end", dateFormatter({ date: validEnd }));
        if (module.name) {
            setItemToStore("date-range", {
                ...storedDateRange,
                [module.name]: {
                    ...(storedDateRange?.[module.name] || {}),
                    ...(module.id ? {
                        [module.id]: {
                            start: dateFormatter({ date: validStart }),
                            end  : dateFormatter({ date: validEnd })
                        }
                    } : {
                        start: dateFormatter({ date: validStart }),
                        end  : dateFormatter({ date: validEnd })
                    })
                }
            });
        }
    };
    
    /* To check if the value is a valid date using strict parsing */
    const isValidDate = (value) => {
        if (typeof value !== "string") return false;

        // Only accept values that strictly match known date formats
        const knownFormats = ["YYYY-MM-DD", "MM/DD/YYYY", "DD-MM-YYYY", "YYYY/MM/DD"];

        return knownFormats.some(format => dayjs(value, format, true).isValid());
    };

    useLayoutEffect(() => {
        if (defaultHandling) handleDefaultDates();
    }, [defaultHandling, rangeAllowed]);

    return {
        dateFormatter,
        control,
        handleDateChange,
        isValidDate
    };
};
export default useDateHandler;